import { For, Show, createEffect, createMemo, createSignal, on, onCleanup, onMount, untrack } from "solid-js";
import type { Placement } from "../game/place";
import { H } from "../game/place";
import { FEEL, causeOf, flightPath, planMoves } from "../game/motion";
import type { DealFlight, Now, Spot, Stand } from "../game/motion";
import type { Feel } from "../game/auto";
import { NO_TILE, tileFile, tileName, tileWords } from "../game/tiles";
import type { Box, Kind } from "./tileArt";
import { BACK, artVersion, sprite, spriteBox, spriteStyle, topFace } from "./tileArt";
import type { Look, Sprite } from "./tileArt";
import { Flight } from "./Flight";
import { milestone } from "./milestones";
import type { TileMark } from "./Review";
import { t } from "../strings";

// Solid compiles these and leaves their types out.
declare module "solid-js" {
  namespace JSX {
    interface HTMLAttributes<T> {
      "style:width"?: string;
      "style:height"?: string;
      "style:transform"?: string;
      "style:z-index"?: string;
      "style:visibility"?: string;
    }
  }
}

// The table's tiles (design 10 §3): one element per physical tile for the whole hand, each on its own
// compositor layer, placed by its transform alone. A move animates that transform on the compositor, sampled
// at the frame budget, so moving a tile lays nothing out and repaints nothing; a tile's picture changes only
// when its face, its look or the way it lies does.
//
// The table is seen from a camera leaning back `angle` from straight above, without perspective (design 10
// T15, ui/tileArt): a tile's place on the felt is projected exactly, its sprite is the picture of its box from
// that camera, and tiles nearer the camera are drawn over those behind. The own row is not on the table: it
// is upright on the screen, where the page placed it.
//
// What moves and how is game/motion's, pure and tested; ui/Flight plays one tile's flight. This puts them on the page.

/** The camera: the board's near edge, about which it leans, and the lean's cosine and sine. */
interface Camera {
  bottom: number;
  c: number;
  s: number;
}

/** A tile's spot: the own row's as placed; a table tile's anchor, the centre of its footprint on the felt
 *  (`rise` tile widths up: a wall's upper tile, a tile lying on a hand), projected. */
function spotOf(p: { x: number; y: number; angle: number; width: number; rise?: number; screen?: boolean }, cam: Camera): Spot {
  if (p.screen) return { x: p.x, y: p.y, angle: p.angle, width: p.width };
  const z = (p.rise ?? 0) * p.width;
  return { x: p.x, y: cam.bottom - (cam.bottom - p.y) * cam.c - z * cam.s, angle: p.angle, width: p.width };
}

/** The picture a placement shows: its sprite and look, the sprite's box and the quarter turns it is drawn in;
 *  `pic` names the picture, so two shots show the same one when their `pic` is equal. */
interface Shot {
  sprite: Sprite | null;
  look: Look;
  pic: string | null;
  box: Box;
  k: number;
}

function shotOf(kind: Kind, name: string, look: Look, angle: number, deg: number): Shot {
  const k = Math.round(angle / 90);
  const o = ((k % 4) + 4) % 4;
  const s = sprite(kind, name, o);
  return { sprite: s, look, pic: s ? `${s.key}|${look}` : null, box: spriteBox(kind, o, deg), k };
}

/** Shows sprite `s` in `look` on a tile's element, unless it already shows it. */
function paint(el: HTMLElement, s: Sprite, look: Look): void {
  const pic = `${s.key}|${look}`;
  if (el.dataset.p === pic) return;
  el.dataset.p = pic;
  for (const [k, v] of Object.entries(spriteStyle(s, look))) el.style.setProperty(k, v);
}

const kindOf = (p: Placement): Kind => (p.screen ? "hand" : p.stand ? "stand" : "flat");
const nameOf = (tile: number, red: boolean) => (tile === NO_TILE ? BACK : tileFile(tile, red));

/** The transform putting `shot`'s sprite with its anchor at the spot, in an element sized for `el` (the
 *  sprite it rests as): the sprite is turned only by what the spot's angle has beyond its own quarter turns,
 *  nothing at rest. */
function transformOf(p: Spot, shot: Shot, el: Box, base: number): string {
  const b = shot.box;
  const sx = (p.width * b.width) / (base * el.width);
  const sy = (p.width * b.height) / (base * el.height);
  const turn = p.angle - 90 * shot.k;
  return `translate(${p.x.toFixed(2)}px, ${p.y.toFixed(2)}px)${turn ? ` rotate(${turn.toFixed(2)}deg)` : ""} scale(${sx.toFixed(4)}, ${sy.toFixed(4)}) translate(${((-b.cx / b.width) * base * el.width).toFixed(2)}px, ${((-b.cy / b.height) * base * el.height).toFixed(2)}px)`;
}

/** The order tiles are drawn in: the table's from the far side to the near, a tile higher up over those
 *  under it; the own row over the table. */
const feltZ = (p: { y: number; rise?: number; width: number }) => 1000 + Math.round(p.y + 10 * (p.rise ?? 0) * p.width);
function zOf(p: Placement): number {
  return p.screen ? 100000 + p.z : feltZ(p);
}
/** A sprite as the values of a flight's keyframe. */
function pictureOf(s: Sprite, look: Look): Keyframe {
  const c = spriteStyle(s, look);
  return { backgroundImage: c["background-image"], backgroundSize: c["background-size"], backgroundPosition: c["background-position"], filter: c.filter || "none" };
}

export interface OwnTile {
  onClick?: () => void; // discard it
  onMove?: (x: number) => void; // auto-sort off: dropped at page x
}

type Was = Stand & Shot & { tile: number };

const px = (n: number) => `${n.toFixed(2)}px`;

export function TileLayer(props: {
  placements: Placement[];
  angle: number; // the camera's lean back from straight above, in degrees: the sprites' (ui/tileArt)
  bottom: number; // the board's near edge, about which it leans, in its pixels
  feel?: Feel;
  onLand?: (p: Placement) => void; // a tile has landed in a river or a meld: the moment for a click or a pulse
  layout?: string; // the table's measured places: a new layout moves every tile at once, not in flight
  hand: string; // the game and hand: tiles are the same wall indices from hand to hand, but new tiles
  dealing: boolean; // the hand is being dealt: new tiles come out of the wall in the deal's order
  mark?: (tile: number) => TileMark | null; // a review's bar over each hand tile (design 12 §5)
  red: boolean;
  base: number; // the sprites' tile width in pixels (scaled per placement)
  fps: number; // the frame budget for motion
  last: Placement | null; // the last discard, outlined
  own: (p: Placement) => OwnTile | null; // the own hand's tiles: what a tap or drag does
  onReady?: () => void; // every tile's element is made (the board shows then)
}) {
  const cam = createMemo<Camera>(() => ({ bottom: props.bottom, c: Math.cos((props.angle * Math.PI) / 180), s: Math.sin((props.angle * Math.PI) / 180) }));
  const byKey = createMemo(() => new Map(props.placements.map((p) => [p.key, p])));
  const shown = createMemo(() => {
    artVersion(); // a sheet shown: the sprites change
    return new Map(props.placements.map((p) => [p.key, shotOf(kindOf(p), nameOf(p.tile, props.red), p.look, p.angle, props.angle)]));
  });
  // A fixed order, so no element ever moves in the page: each tile's element lives as long as its key.
  const keys = createMemo(
    () => props.placements.map((p) => p.key).sort(),
    [] as string[],
    { equals: (a, b) => a.length === b.length && a.every((k, i) => k === b[i]) },
  );
  const els = new Map<string, HTMLElement>();
  // Each tile's flight under way, kept here: asking the browser for an element's animations recomputes its style, and
  // asked of every tile at a deal it cost a slow phone ~150 ms.
  const flights = new Map<string, Flight>();
  // What a flight of the deal set out with: the server waits the deal out (design 10 T18), but a page a moment late to its table
  // starts it late, and a row closing up then only moves where its tiles land.
  const inDeal = new WeakMap<Flight, DealFlight<Was>>();
  let prev = new Map<string, Was>();
  let seen = "";
  // The tile a finger has raised: one at a time, and down again at a touch anywhere but on the hand.
  const [raised, setRaised] = createSignal<string | null>(null);
  const [cue, setCue] = createSignal(secondTaps() < CUE_UNTIL);
  createEffect(() => {
    if (!raised()) return;
    const lower = (e: PointerEvent) => {
      if (!(e.target as Element).closest?.(".tile-hit")) setRaised(null);
    };
    addEventListener("pointerdown", lower, true);
    onCleanup(() => removeEventListener("pointerdown", lower, true));
  });
  const secondTap = () => {
    const n = secondTaps() + 1;
    try {
      localStorage.setItem(TAPS_KEY, String(n));
    } catch {
      // no storage here: the cue goes when this page does
    }
    if (n >= CUE_UNTIL) setCue(false);
  };
  let laidOut = props.layout ?? "";
  // The tiles' elements are first made a batch at a time, a task each, so making them and the browser's
  // drawing them never hold the main thread long (design 11 §3: no task over 50 ms; all at once it was ~100
  // ms on a slow phone). The board is hidden until the last batch is in (ui/Table); a board with no tiles to draw
  // yet shows at once, and the batches then start when there are, the layer hidden until they are in, so that no tile
  // shows in its place before the deal takes it from the wall. A tile is made once it has a picture: the batches wait for
  // every tile's, or the sheets' arrival made them all in one task (owner, 2026-10-31).
  const BATCH = 40;
  const [made, setMade] = createSignal(BATCH);
  let ready = () => {
    props.onReady?.();
    ready = () => {};
  };
  const more = () => {
    if (made() >= keys().length) {
      setMade(Infinity); // from now on every tile at once: a tile's element then lives as long as its key
      return ready();
    }
    setTimeout(() => {
      setMade((n) => n + BATCH);
      more();
    }, 0);
  };
  const pictured = createMemo(() => {
    const shots = shown();
    return props.placements.length > 0 && props.placements.every((p) => shots.get(p.key)?.sprite);
  });
  onMount(() => {
    if (!pictured()) ready();
  });
  createEffect(
    on(pictured, (all) => {
      if (all && made() !== Infinity) more();
    }),
  );
  const madeKeys = createMemo(() => (made() >= keys().length ? keys() : keys().slice(0, made())));

  // After each change has reached the page: what moved flies there from where it was.
  createEffect(() => {
    const placements = props.placements;
    const shots = shown();
    const c = cam();
    const scene = `${props.hand}@${props.angle}`;
    const layout = props.layout ?? "";
    const dealing = props.dealing;
    const feel = props.feel ?? "weighty";
    const base = props.base;
    const fps = props.fps;
    const built = made() === Infinity;
    untrack(() => {
      // The startup milestones (design 11 §3): the own hand's pictures, then every tile's.
      if (placements.some((p) => p.screen && shots.get(p.key)?.sprite)) milestone("mj:hand");
      if (placements.length && placements.every((p) => shots.get(p.key)?.sprite)) milestone("mj:tiles");
      // Nothing is on the table until every tile's element is made and its picture drawn: a deal then takes all its tiles,
      // not the first batch's. A tile counted as seen before it had a picture was not new when it came, and in a browser's
      // first game, the sheets still being drawn as the hand arrives, no deal played at all (owner, 2026-10-31).
      if (!built || placements.some((p) => !shots.get(p.key)?.sprite)) return;
      // A new hand is a new set of tiles: nothing flies from where the last hand left it. Nor does anything
      // fly when the camera moves: the table is simply seen anew. A new layout (a resize, the fonts arriving)
      // moves every tile to its new place at once, a flight in progress included; the deal and the moves of
      // the game are not replayed.
      if (seen !== scene) {
        seen = scene;
        prev = new Map();
      }
      const relaid = laidOut !== layout && prev.size > 0; // with nothing placed yet there is nothing to move: the deal still plays
      if (relaid || !prev.size) {
        for (const f of flights.values()) f.cancel();
        flights.clear();
      }
      laidOut = layout;
      const before = prev;
      const spec = FEEL[feel];
      const tiles: Now<Was>[] = placements.map((p) => {
        const home = !before.size && dealing && p.home ? { ...p.home, screen: false } : null;
        return {
          key: p.key,
          at: { ...spotOf(p, c), ...shots.get(p.key)!, where: p.where, seat: p.seat, tile: p.tile },
          deal: p.deal,
          wall: home ? { ...spotOf(home, c), ...shotOf("flat", BACK, "plain", home.angle, props.angle), where: "wall", seat: p.seat, tile: NO_TILE } : undefined,
        };
      });
      const fresh = tiles.filter((t) => !before.has(t.key)).length;
      const { moves, landed } = planMoves(tiles, before, causeOf(before.size, fresh, dealing, relaid), spec, (key) => {
        const f = flights.get(key);
        return f?.running ? { pose: f.pose, deal: inDeal.get(f) } : undefined;
      });
      prev = new Map(tiles.map((t) => [t.key, t.at]));
      // Every tile's own style says how it rests, flying or not: a flight only overrides that while it lasts (ui/Flight).
      for (const { key, at } of tiles) {
        const el = els.get(key);
        if (el && at.sprite) paint(el, at.sprite, at.look);
      }
      const placed = byKey();
      for (const { key } of landed) props.onLand?.(placed.get(key)!);
      for (const m of moves) {
        const el = els.get(m.key);
        const p = placed.get(m.key)!;
        if (!el) continue;
        const path = flightPath(m.from, m.to, m.ms, m.turn, spec!, fps);
        const frames: Keyframe[] = path.map((pose, i) => ({
          offset: i / (path.length - 1),
          transform: transformOf(pose, pose.early ? m.from! : m.to, m.to.box, base),
          ...(m.from ? {} : { opacity: pose.opacity }),
          easing: "steps(1, end)", // drawn only when the picture changes: at most `fps` frames a second
        }));
        if (m.kind === "aim") {
          flights.get(m.key)!.aim(path, frames);
          continue;
        }
        flights.get(m.key)?.cancel();
        const flight: Flight = new Flight(el, {
          path,
          frames,
          ms: m.ms,
          delay: m.delay,
          picture: m.turn && m.from?.sprite ? pictureOf(m.from.sprite, m.from.look) : undefined,
          // Waiting in the wall, it lies in its stack's order; waiting to close up, in its place's.
          waitZ: m.kind === "deal" ? String(feltZ(p.home!)) : m.delay ? el.style.zIndex : undefined,
          onLand: () => {
            if (flights.get(m.key) === flight) flights.delete(m.key);
            if (m.from && (p.where === "river" || p.where === "meld") && m.from.where !== p.where) props.onLand?.(p);
          },
        });
        flights.set(m.key, flight);
        if (m.kind === "deal") inDeal.set(flight, { from: m.from!, ms: m.ms, delay: m.delay });
      }
    });
  });

  // The last discard's ring, round the top of its box.
  const ring = createMemo(() => {
    const last = props.last;
    if (!last || last.screen) return null;
    const k = Math.round(last.angle / 90);
    const top = topFace(((k % 4) + 4) % 4, props.angle);
    const s = spotOf(last, cam());
    const w = top.width * last.width;
    const h = top.height * last.width;
    return { w, h, z: zOf(last) + 1, x: s.x - w / 2, y: s.y - top.up * last.width - h / 2 };
  });
  // The own hand first, left to right, as Tab and a screen reader go through it: they went by tile id, jumping across the hand (UX review 2026-11-02). The elements
  // move only when the hand does. The rest stay as they are, for their marks.
  const handKeys = createMemo(
    () => {
      const made = new Set(madeKeys());
      const own = props.placements.filter((p) => made.has(p.key) && p.screen && (p.where === "hand" || p.where === "drawn")).sort((a, b) => a.x - b.x).map((p) => p.key);
      const mine = new Set(own);
      return [...own, ...madeKeys().filter((k) => !mine.has(k))];
    },
    [] as string[],
    { equals: (a, b) => a.length === b.length && a.every((k, i) => k === b[i]) },
  );
  return (
    <div class="tile-layer" style:visibility={made() === Infinity ? "visible" : "hidden"}>
      <For each={madeKeys()}>
        {(key) => {
          const tile = createMemo(() => {
            const p = byKey().get(key);
            const shot = shown().get(key);
            return p && shot?.sprite ? { p, shot, sprite: shot.sprite } : null;
          });
          return (
            <Show when={tile()}>
              {(it) => (
                <span
                  ref={(el) => {
                    els.set(key, el);
                    onCleanup(() => void (els.get(key) === el && els.delete(key)));
                    // Painted, not bound with the rest: a style object is written whole at each update, on every tile.
                    untrack(() => paint(el, it().sprite, it().shot.look));
                  }}
                  class="tile-sprite"
                  aria-hidden="true"
                  style:width={`${props.base * it().shot.box.width}px`}
                  style:height={`${props.base * it().shot.box.height}px`}
                  style:transform={transformOf(spotOf(it().p, cam()), it().shot, it().shot.box, props.base)}
                  style:z-index={String(zOf(it().p))}
                />
              )}
            </Show>
          );
        }}
      </For>
      {/* A new element per discard, appearing once the tile can have landed (the longest flight of the feel):
          it outlined the empty slot while the tile flew (audit 2026-10-14). */}
      <Show when={props.last?.key} keyed>
        {(_key) => (
          <Show when={ring()}>
            {(r) => (
              <span
                class="last-discard"
                style={{
                  width: px(r().w),
                  height: px(r().h),
                  "z-index": String(r().z),
                  transform: `translate(${px(r().x)}, ${px(r().y)})`,
                  "animation-delay": `${FEEL[props.feel ?? "weighty"]?.max ?? 0}ms`,
                }}
              />
            )}
          </Show>
        )}
      </Show>
      <For each={handKeys()}>
        {(key) => {
          const p = () => byKey().get(key);
          const o = createMemo(() => {
            const q = p();
            return q ? props.own(q) : null;
          });
          const m = createMemo(() => {
            const q = p();
            return q && o() && props.mark ? props.mark(q.tile) : null;
          });
          return (
            <>
              <Show when={o()}>
                {(oo) => (
                  <HitTile
                    p={p()!}
                    o={oo()}
                    red={props.red}
                    img={() => els.get(key)}
                    raised={raised() === key}
                    raise={(on) => setRaised((r) => (on ? key : r === key ? null : r))}
                    glide={(props.feel ?? "weighty") !== "instant"}
                    onSecondTap={secondTap}
                  />
                )}
              </Show>
              <Show when={m()}>{(mm) => <TileMarkBar p={p()!} m={mm()} />}</Show>
            </>
          );
        }}
      </For>
      <Show when={cue() && raised() ? byKey().get(raised()!) : null}>{(q) => <TapAgain p={q()} red={props.red} />}</Show>
    </div>
  );
}

const DRAG_PX = 30; // from where the press began: upward discards, sideways moves
const HOVER_LIFT = 0.18; // of a tile's height, under a mouse or the keyboard's focus
const RAISE_LIFT = 0.35; // raised by a finger, which hides more of it
const GLIDE_MS = 160; // what is left of a lift or a drag as its tile sets out

// Words over the raised tile for a player's first few second taps, on this device: a tile that only rises is otherwise a tap that seems not to have worked.
const TAPS_KEY = "mj.second-taps";
const CUE_UNTIL = 3;
function secondTaps(): number {
  try {
    return Number(localStorage.getItem(TAPS_KEY)) || 0;
  } catch {
    return 0;
  }
}

function TapAgain(props: { p: Placement; red: boolean }) {
  const h = () => props.p.width * H;
  return (
    <span
      ref={(el) => {
        // Over its tile, and on the screen: the hand runs to the screen's edges.
        createEffect(() => {
          void props.p.x;
          el.style.marginLeft = "";
          const r = el.getBoundingClientRect();
          const out = r.left < 8 ? 8 - r.left : r.right > innerWidth - 8 ? innerWidth - 8 - r.right : 0;
          if (out) el.style.marginLeft = `${out}px`;
        });
      }}
      class="tap-again"
      style={{ left: `${props.p.x}px`, top: `${props.p.y - h() / 2 - RAISE_LIFT * h() - 6}px` }}
      aria-hidden="true"
    >
      {tileWords(props.p.tile, props.red)} · {t.pressAgain}
    </span>
  );
}

/** A review's bar over a hand tile (design 12 §5): its height the discard's value against the best, its colour
 *  the class; the best outlined, the tile chosen marked above its bar. Placed as the tile's button is. */
function TileMarkBar(props: { p: Placement; m: TileMark }) {
  const turned = () => Math.abs(props.p.angle) % 180 === 90;
  const w = () => (turned() ? props.p.width * H : props.p.width);
  const h = () => (turned() ? props.p.width : props.p.width * H);
  const full = () => 0.8 * h();
  const bar = () => Math.max(3, full() * props.m.height);
  return (
    <>
      <span
        class={`tile-mark cls-${props.m.cls}${props.m.best ? " best" : ""}`}
        style={{ width: `${0.55 * w()}px`, height: `${bar()}px`, transform: `translate(${props.p.x - 0.275 * w()}px, ${props.p.y - h() / 2 - 4 - bar()}px)` }}
        aria-hidden="true"
      />
      <Show when={props.m.chosen}>
        {/* Above its bar, where it is seen: beneath the tile it fell off the screen's edge (audit 2026-10-15). */}
        <span class="tile-chosen" style={{ transform: `translate(${props.p.x - 7}px, ${props.p.y - h() / 2 - 4 - bar() - 12}px)` }} aria-hidden="true" />
      </Show>
    </>
  );
}

/** The own hand's tile under the finger. A finger's first tap raises it and a second on it discards it: a tile is narrower than a fingertip,
 *  and a discard cannot be taken back. A mouse's click and the keyboard discard at once, and so does a screen reader's press, which says what
 *  it does. Dragged up it discards at once, whatever did it; dragged sideways it moves (auto-sort off). An invisible button over the tile; the
 *  tile's picture follows the finger. */
function HitTile(props: { p: Placement; o: OwnTile; red: boolean; img: () => HTMLElement | undefined; raised: boolean; raise: (on: boolean) => void; glide: boolean; onSecondTap: () => void }) {
  let press: { x: number; y: number } | null = null;
  let dragged = false;
  let finger = false; // what the press under way came from
  const [hover, setHover] = createSignal(false);
  const turned = () => Math.abs(props.p.angle) % 180 === 90;
  const w = () => (turned() ? props.p.width * H : props.p.width);
  const h = () => (turned() ? props.p.width : props.p.width * H);
  const lift = () => (props.raised ? RAISE_LIFT * h() : 0);
  // Where it rests when nothing is holding it.
  const rest = () => {
    if (!props.o.onClick) return "";
    if (props.raised) return `0 ${-lift()}px`;
    return hover() ? `0 ${-HOVER_LIFT * h()}px` : "";
  };
  createEffect(() => {
    const el = props.img();
    if (el && !press) el.style.translate = rest();
  });
  // Over a drawn tile lying on top of the hand, into which it rises; its own button over that tile's too.
  createEffect(() => {
    const el = props.img();
    if (el) el.style.zIndex = String(zOf(props.p) + (props.raised ? 10 : 0));
  });
  // Its turn over with it raised: down, and not raised when the next turn comes.
  createEffect(() => {
    if (props.raised && !props.o.onClick) props.raise(false);
  });
  onCleanup(() => {
    if (props.raised) props.raise(false);
    const el = props.img();
    if (el) el.style.translate = "";
  });
  const release = () => {
    const el = props.img();
    press = null;
    if (el) el.style.translate = rest();
  };
  // Goes, from where it is: the flight sets out from its place in the hand, and what it was lifted or dragged by runs out as it does.
  const discard = () => {
    const el = props.img();
    const from = el?.style.translate;
    press = null;
    props.raise(false);
    props.o.onClick?.();
    if (el) el.style.translate = "";
    if (el && from && props.glide) el.animate([{ translate: from }, { translate: "0 0" }], { duration: GLIDE_MS, easing: "steps(5, end)" });
  };
  const name = () => tileName(props.p.tile, props.red);
  return (
    <button
      type="button"
      class="tile-hit"
      style={{ width: `${w()}px`, height: `${h() + lift()}px`, transform: `translate(${props.p.x - w() / 2}px, ${props.p.y - h() / 2 - lift()}px)`, "z-index": props.raised ? 1 : undefined }}
      aria-label={props.o.onClick ? `discard ${name()}` : props.o.onMove ? `move ${name()}` : name()}
      title={tileWords(props.p.tile, props.red)}
      disabled={!props.o.onClick && !props.o.onMove}
      onKeyDown={(e) => {
        // Arrows go along the hand; with Shift they carry the tile, as a drag does.
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        const row = [...document.querySelectorAll<HTMLElement>(".tile-hit:not(:disabled)")].sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
        const at = row.indexOf(e.currentTarget);
        const step = e.key === "ArrowLeft" ? -1 : 1;
        e.preventDefault();
        e.stopPropagation();
        const next = row[at + step];
        if (!next) return;
        if (e.shiftKey && props.o.onMove) {
          const r = next.getBoundingClientRect();
          props.o.onMove(r.left + r.width / 2 + (step * r.width) / 4);
          const here = e.currentTarget;
          setTimeout(() => here.isConnected && here.focus(), 0);
        } else next.focus();
      }}
      onPointerEnter={(e) => setHover(e.pointerType === "mouse")}
      onPointerLeave={() => setHover(false)}
      onFocus={(e) => setHover(e.currentTarget.matches(":focus-visible"))}
      onBlur={() => setHover(false)}
      onClick={() => {
        const tap = finger;
        finger = false;
        press = null; // a click ends a press, whatever became of its pointer's last events
        if (dragged) dragged = false;
        else if (!props.o.onClick) return;
        else if (tap && !props.raised) props.raise(true);
        else {
          if (tap) props.onSecondTap();
          discard();
        }
      }}
      onPointerDown={(e) => {
        press = { x: e.clientX, y: e.clientY };
        dragged = false;
        finger = e.pointerType !== "mouse";
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        const at = press;
        const el = props.img();
        if (!at || !el) return;
        const dx = props.o.onMove ? e.clientX - at.x : 0;
        const dy = props.o.onClick ? Math.min(0, e.clientY - at.y) - lift() : 0;
        el.style.translate = dx || dy ? `${dx}px ${dy}px` : "";
      }}
      onPointerUp={(e) => {
        const at = press;
        if (!at) return release();
        if (props.o.onClick && at.y - e.clientY > DRAG_PX) {
          dragged = true;
          discard();
          return;
        }
        release();
        if (props.o.onMove && Math.abs(e.clientX - at.x) > DRAG_PX) {
          dragged = true;
          props.o.onMove(e.clientX);
        }
      }}
      onPointerCancel={release}
    />
  );
}
